import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import ContainerMaxWidthXl from '@deltek/specification-client/core/Surfaces/ContainerMaxWidthXl'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import { Button, CircularProgress, Divider } from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import { AssignmentTurnedInOutlined } from '@material-ui/icons'
import SettingsIcon from '@material-ui/icons/Settings'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { ProjectElementNodeWithMetadata } from 'api/graphQL/types/projectElement.types'
import { ActionButton } from 'components/ActionBar'
import { ToolTipText } from 'components/ActionBar/ToolTipText'
import {
    useActionBarActions,
    useIsPDFExportLoading,
    useIsSupportingDocsLoading,
    useIsWordExportLoading,
    usePDFExportURL,
    useSupportingDocsUrl,
    useWordExportURL,
} from 'components/WorkspaceEditor/ActionBarStore'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useActionBarHandler } from 'components/WorkspaceEditor/hooks/useActionBarHandler'
import { useWorkspace2ActionBarStyles } from 'components/WorkspaceEditor/styles/useWorkspace2ActionBarStyles'
import { handleViewProductsWithElementItem } from 'components/WorkspaceEditor/utils/productLibraryUtils'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import {
    useIsTreeLoading,
    useShallowSelector,
    useWorkspace2Actions,
} from 'components/WorkspaceEditor/Workspace2Store'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import AddRemoveProjectButton from 'pages/ProjectsPage/WorkspacePage/components/AddRemoveProjectButton'
import {
    ActionButtonVariant,
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import ShowHideToggleButton from 'pages/ProjectsPage/WorkspacePage/components/ShowHideToggleButton'
import { WorkspaceActionBarContext } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarContext'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { HiClipboardDocumentList } from 'shared/HarmonyIcon'
import { useFirmCreatedDate } from 'shared/hooks/useFirmCreatedDate'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { useShouldHideWorkspaceToggle } from 'shared/hooks/useShouldHideWorkspaceToggle'
import { useWorkspaceToggleSync } from 'shared/hooks/useWorkspaceToggleSync'
import { getNestedObjectPropertyValue } from 'shared/utils/objectUtils'
import {
    getDialogUrl,
    getFromLocalStorage,
    updatePersistentConfigs,
} from 'shared/utils/workspaceUtils'
import { ExpandCollapseButton } from './ExpandCollapseButton'
import ExportPDFIcon from './ExportPDFIcon'
import ExportWordIcon from './ExportWordIcon'
import PreviewIcon from './PreviewIcon'
import ProductLibraryIcon from './ProductLibraryIcon'
import ProjectInfo from './ProjectInfo'
import SMMActionButtons from './SMMActionButtons'
import { useProjectTasksSync } from './useProjectTasksSync'

// Constants for tab indices
const TAB_INDICES = {
    PART_1_GENERAL: 1,
    PART_2_PRODUCTS: 2,
    PART_3_EXECUTION: 3,
} as const

// Service worker message delay for dialog initialization
const SERVICE_WORKER_MESSAGE_DELAY_MS = 500

// Extended interface for project element nodes with additional properties
// These properties are used but not defined in the base interface
interface ProjectElementNodeExtended extends ProjectElementNodeWithMetadata {
    parentElementId?: string
}

export interface Workspace2ActionBarItem {
    label?: string
    stateKey: string
}
interface AddRemoveProjectProps {
    updateElementList?: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[]
    ) => void
    isOnlyAddedToProject?: boolean
    selectedElementFormat?: string
    allElementsList?: ProjectElementNode[]
}
export interface Workspace2ActionBarProps {
    selectedProjectNode?: ProjectElementNodeExtended | null
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    userId?: string
    projectId?: string
    projectName?: string
    isLoading: boolean
    onProjectSettingsClicked?: () => void
    contentTeamFromStudioPage?: boolean
    addRemoveProjectProps?: AddRemoveProjectProps
}

interface LocationState {
    from: string
    fromStudio?: boolean
    selectedElementIdFromDesignProgressTable?: string
}

const Workspace2ActionBar = ({
    selectedProjectNode,
    selectedTopic,
    userId,
    projectId = '',
    projectName = '',
    isLoading,
    onProjectSettingsClicked,
    contentTeamFromStudioPage,
    addRemoveProjectProps,
}: Workspace2ActionBarProps) => {
    const { pathname, state } = useLocation()

    const { currentState, setStateValue } = React.useContext(
        WorkspaceActionBarContext
    )

    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const {
        elementItemValue,
        setElementItemValue,
    } = useElementItemShallowSelector((s) => ({
        elementItemValue: s.elementItemValue,
        setElementItemValue: s.setElementItemValue,
    }))

    const { activeTab, selectedProjectElementNode } = useShallowSelector(
        (state) => ({
            activeTab: state.activeTab,
            selectedProjectElementNode: state.selectedProjectElementNode,
        })
    )

    const isExportPDFLoading = useIsPDFExportLoading()
    const isExportWordLoading = useIsWordExportLoading()
    const isSupportingDocumentsLoading = useIsSupportingDocsLoading()
    const pdfExportUrl = usePDFExportURL()
    const wordExportUrl = useWordExportURL()
    const supportingDocsUrl = useSupportingDocsUrl()
    const { setExportUrl, setSupportingDocsUrl } = useActionBarActions()
    const isUserRedirectedFromStudio = React.useMemo(
        () =>
            (state as LocationState)?.fromStudio ||
            pathname.includes('/DraftMasterSpecProject/') ||
            pathname.includes(
                `/${currentUserActiveFirm?.bestPracticeProjectId}/`
            ),
        [state, pathname]
    )

    const classes = useWorkspace2ActionBarStyles({
        isOMorSMM: isUserRedirectedFromStudio,
    })

    // Derive projectProductFamilyId from selectedProjectNode
    const projectProductFamilyId = useMemo(() => {
        if (!selectedProjectNode) return ''

        if (selectedProjectNode.elementType === 'PRODUCTFAMILY') {
            return selectedProjectNode.elementId || ''
        } else if (selectedProjectNode.elementType === 'PRODUCTTYPE') {
            return elementItemValue.parentFamilyId || ''
        }
        return ''
    }, [selectedProjectNode, elementItemValue])

    const {
        handleCollapseAll,
        handleExpandAll,
        handleExportPDF,
        handleExportWord,
        handleViewPrintPreview,
        handleViewSupportingDocs,
        keysToExpand,
        expandCollapseDisabled,
    } = useActionBarHandler(projectId, projectProductFamilyId)

    // Fetch firm creation date
    const { firmCreatedDate } = useFirmCreatedDate()

    // Check if workspace toggle should be hidden based on firm creation date
    const shouldHideWorkspaceToggle = useShouldHideWorkspaceToggle({
        firmCreatedDate,
    })

    const handleToggleSwitchValueChange = React.useCallback(
        (stateKey: string) => {
            if (stateKey.includes('betaWorkspace')) {
                const currentValue = getFromLocalStorage(
                    userId ?? '',
                    'Global',
                    'betaWorkspaceToggleChecked'
                )
                // Default to false (classic workspace) if no value is stored (null)
                // New firms (>= cutoff date) will have toggle hidden via useShouldHideWorkspaceToggle
                const toggledValue = !(currentValue === null
                    ? false
                    : currentValue)

                if (toggledValue) {
                    setElementItemValue({
                        ...elementItemValue,
                        elementId: '',
                        elementType: '',
                        baseElementId: '',
                    })
                }

                updatePersistentConfigs(
                    userId,
                    'Global',
                    'betaWorkspaceToggleChecked',
                    toggledValue
                )

                setStateValue('showHideStates.betaWorkspace', toggledValue)
                return
            }

            setStateValue(
                stateKey,
                !getNestedObjectPropertyValue(stateKey, currentState),
                true
            )
        },
        [currentState, setStateValue]
    )

    const { switchWorkspace } = useWorkspace2Actions()
    const isTreeLoading = useIsTreeLoading()

    // Synchronize workspace toggle based on firm creation date and user preferences
    useWorkspaceToggleSync(
        shouldHideWorkspaceToggle,
        userId,
        setStateValue,
        switchWorkspace
    )

    const checkedState = React.useCallback(
        (
            barItem: Workspace2ActionBarItem,
            currentState: Record<string, any>,
            isInProject: boolean | undefined
        ) => {
            const { stateKey, label } = barItem
            if (
                label &&
                ['Project Properties', 'Track Changes'].includes(label) &&
                !isInProject
            ) {
                return false
            }
            return getNestedObjectPropertyValue(stateKey, currentState)
        },
        []
    )
    const evaluationsDisabled = useMemo(
        () =>
            !(
                selectedProjectNode?.elementType !== 'ASSEMBLY' || selectedTopic
            ) ||
            isLoading ||
            isTreeLoading,
        [selectedProjectNode, selectedTopic, isLoading, isTreeLoading]
    )
    const exportDisabled = useMemo(
        () => evaluationsDisabled || !selectedProjectNode?.isInProject,
        [selectedProjectNode, evaluationsDisabled]
    )

    // Element type checks
    const selectedElementType = selectedProjectNode?.elementType

    // Enable Product Library button when on Products tab (Part 2) or when a Product Type element is selected
    const isProductLibraryDisabled = useMemo(() => isLoading, [isLoading])

    const handleViewProducts = useCallback(() => {
        handleViewProductsWithElementItem(
            selectedProjectNode,
            elementItemValue,
            userId ?? '',
            projectId || ''
        )
    }, [selectedProjectNode, elementItemValue, userId, projectId])
    useEffect(() => {
        if (pdfExportUrl) {
            navigateToExternalLink(pdfExportUrl, true)
            setExportUrl('pdf', '')
        }
        if (wordExportUrl) {
            navigateToExternalLink(wordExportUrl, true)
            setExportUrl('docx', '')
        }
        if (supportingDocsUrl) {
            window?.open(supportingDocsUrl, '_blank')?.focus()
            setSupportingDocsUrl('')
        }
    }, [pdfExportUrl, wordExportUrl, supportingDocsUrl])

    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

    const classicWorkspaceLabel = contentTeamFromStudioPage
        ? 'Switch to Classic OM Workspace'
        : 'Switch to Classic Workspace'
    const { showSMMActionButtons } = useReactiveVar(reactiveWorkspaceActions)

    const { isProjectTasksEnabled, taskCount } = useProjectTasksSync({
        projectId,
        selectedProjectNode,
        isLoading: isLoading || isTreeLoading,
    })

    // Build the tasks window URL including the current element context so the Tasks
    // page opens already scoped to the selected element — mirrors notesUtils.handleViewNotes.
    const openTasksWindow = React.useCallback(() => {
        if (!projectId) return

        let family = ''
        let productType = ''
        const elementType = selectedProjectNode?.elementType

        if (elementType === 'PRODUCTFAMILY') {
            family = selectedProjectNode?.text?.split('|').join(' - ') ?? ''
        } else if (elementType === 'PRODUCTTYPE') {
            const treeSegments = (selectedProjectNode?.treePath ?? '')
                .split('||')
                .filter(Boolean)
            const parentSegment = treeSegments[treeSegments.length - 2] ?? ''
            family = parentSegment.split('|').filter(Boolean).join(' - ')
            productType =
                selectedProjectNode?.text?.split('|').join(' - ') ?? ''
        }

        const url = getDialogUrl(
            {
                projectId,
                elementNode: (selectedProjectNode as unknown) as ProjectElementNode,
                family,
                productType,
                familyId: projectProductFamilyId || undefined,
            },
            'projecttasks'
        )

        window.open(
            url,
            `ProjectTasks${projectId}`,
            `directories=no,titlebar=no,location=no,toolbar=no,menubar=no,resizable=yes,height=${window.screen.height},width=${window.screen.width}`
        )
    }, [projectId, selectedProjectNode, projectProductFamilyId])

    const loadingIconSize = 18

    const iconClass = useMemo(
        () =>
            exportDisabled
                ? classes.iconButtonDisabledExtended
                : classes.iconButtonExtended,
        [exportDisabled, classes]
    )
    // handles action bar shortcut keys
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const shortcuts: Array<{
                key: keyof typeof ShortcutKeys
                condition: boolean
                action: () => void
            }> = [
                {
                    key: 'ExportPDF',
                    condition: !exportDisabled && !isExportPDFLoading,
                    action: handleExportPDF,
                },
                {
                    key: 'ExportWord',
                    condition: !exportDisabled && !isExportWordLoading,
                    action: handleExportWord,
                },
                {
                    key: 'Preview2',
                    condition: !exportDisabled,
                    action: handleViewPrintPreview,
                },
                {
                    key: 'Evaluations',
                    condition:
                        !isSupportingDocumentsLoading && !evaluationsDisabled,
                    action: handleViewSupportingDocs,
                },
            ]

            for (const { key, condition, action } of shortcuts) {
                if (tryGetShortcutKey(key, event) && condition) {
                    event.preventDefault()
                    action()
                    return // Exit early after handling a shortcut
                }
            }
        },
        [
            exportDisabled,
            evaluationsDisabled,
            handleExportPDF,
            handleExportWord,
            handleViewPrintPreview,
            handleViewSupportingDocs,
            isExportPDFLoading,
            isExportWordLoading,
            isSupportingDocumentsLoading,
        ]
    )
    // register keydown event listener and clean it up on unmount
    React.useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    return (
        <React.Fragment>
            <AppBar
                elevation={0}
                position="relative"
                className={classes.showHideContainer}
            >
                <ContainerMaxWidthXl>
                    <GridContainer className={classes.contentContainer}>
                        <GridItem
                            xs={3}
                            className={classes.projectTitleContainer}
                        >
                            {(!contentTeamFromStudioPage ||
                                isCurrentlyOnOMWorkspace) && (
                                <ProjectInfo
                                    projectName={projectName}
                                    selectedProjectNode={selectedProjectNode!}
                                    isCurrentlyOnOMWorkspace={
                                        isCurrentlyOnOMWorkspace
                                    }
                                />
                            )}
                        </GridItem>
                        <GridItem xs={6} className={classes.actionContainer}>
                            <Button
                                data-testid="viewProductsButton"
                                onClick={() => handleViewProducts()}
                                className={classes.productLibraryButton}
                                startIcon={<ProductLibraryIcon />}
                                disabled={isProductLibraryDisabled}
                                aria-label="Product Library"
                            >
                                Product Library
                            </Button>
                            <Divider
                                orientation="vertical"
                                className={classes.divider}
                            />
                            {!contentTeamFromStudioPage ? (
                                <HoverTooltip
                                    text={
                                        <ToolTipText
                                            label="Evaluations"
                                            smallLabel={
                                                ShortcutKeys.Evaluations
                                            }
                                        />
                                    }
                                >
                                    <span>
                                        <Button
                                            data-testid="evaluationsButton"
                                            onClick={handleViewSupportingDocs}
                                            className={
                                                classes.evaluationsButton
                                            }
                                            startIcon={
                                                isSupportingDocumentsLoading ? (
                                                    <CircularProgress
                                                        size={16}
                                                        className={
                                                            classes.loadingIcon
                                                        }
                                                    />
                                                ) : (
                                                    <AssignmentTurnedInOutlined />
                                                )
                                            }
                                            disabled={
                                                evaluationsDisabled ||
                                                isSupportingDocumentsLoading
                                            }
                                            aria-label="Evaluations"
                                        >
                                            Evaluations
                                        </Button>
                                    </span>
                                </HoverTooltip>
                            ) : (
                                showSMMActionButtons && <SMMActionButtons />
                            )}
                            <ActionButton
                                onClick={handleViewPrintPreview}
                                disabled={exportDisabled}
                                variant={ActionButtonVariant.Preview}
                                tooltip={{
                                    label: 'Preview',
                                    smallLabel: ShortcutKeys.Preview2,
                                }}
                                aria-label="Preview"
                                iconOverlaySize={26}
                            >
                                <PreviewIcon className={iconClass} />
                            </ActionButton>
                            {!contentTeamFromStudioPage && (
                                <ActionButton
                                    onClick={handleExportPDF}
                                    disabled={
                                        exportDisabled || isExportPDFLoading
                                    }
                                    variant={ActionButtonVariant.Export}
                                    tooltip={{
                                        label: 'Export to PDF',
                                        smallLabel: ShortcutKeys.ExportPDF,
                                    }}
                                    aria-label="Export to PDF"
                                    iconOverlaySize={26}
                                    enableTooltipOnDisabled={true}
                                >
                                    {isExportPDFLoading ? (
                                        <CircularProgress
                                            size={loadingIconSize}
                                            className={classes.loadingIcon}
                                        />
                                    ) : (
                                        <ExportPDFIcon className={iconClass} />
                                    )}
                                </ActionButton>
                            )}
                            <ActionButton
                                onClick={handleExportWord}
                                disabled={exportDisabled || isExportWordLoading}
                                variant={ActionButtonVariant.Export}
                                tooltip={{
                                    label: 'Export to Doc/Word',
                                    smallLabel: ShortcutKeys.ExportWord,
                                }}
                                aria-label="Export to Doc/Word"
                                iconOverlaySize={26}
                                enableTooltipOnDisabled={true}
                            >
                                {isExportWordLoading ? (
                                    <CircularProgress
                                        size={loadingIconSize}
                                        className={classes.loadingIcon}
                                    />
                                ) : (
                                    <ExportWordIcon className={iconClass} />
                                )}
                            </ActionButton>
                            <Divider
                                orientation="vertical"
                                className={classes.divider}
                            />
                            <ExpandCollapseButton
                                disabled={expandCollapseDisabled}
                                onExpandAll={handleExpandAll}
                                onCollapseAll={handleCollapseAll}
                                isExpanded={keysToExpand?.length == 0}
                            />
                            {!contentTeamFromStudioPage && (
                                <>
                                    <ActionButton
                                        onClick={onProjectSettingsClicked}
                                        variant={ActionButtonVariant.Settings}
                                        tooltip={{
                                            label: 'Workspace Settings',
                                        }}
                                        aria-label="Workspace Settings"
                                        iconOverlaySize={26}
                                    >
                                        <SettingsIcon
                                            className={classes.iconButton}
                                        />
                                    </ActionButton>

                                    <AddRemoveProjectButton
                                        projectId={projectId}
                                        projectElementNode={
                                            selectedProjectNode!
                                        }
                                        contentTeamFromStudioPage={
                                            contentTeamFromStudioPage
                                        }
                                        {...addRemoveProjectProps}
                                    />
                                </>
                            )}
                        </GridItem>
                        <GridItem
                            key={'showHideStates.betaWorkspace'}
                            className={classes.switchContainer}
                            xs={3}
                            style={{
                                visibility: shouldHideWorkspaceToggle
                                    ? 'hidden'
                                    : 'visible',
                            }}
                        >
                            {isProjectTasksEnabled && (
                                <Button
                                    data-testid="tasksButton"
                                    onClick={openTasksWindow}
                                    className={classes.tasksButton}
                                    startIcon={
                                        <HiClipboardDocumentList size={16} />
                                    }
                                    disabled={
                                        isLoading || isTreeLoading || !projectId
                                    }
                                    aria-label="Tasks"
                                >
                                    Tasks
                                    {taskCount > 0 && (
                                            <span
                                                className={classes.tasksBadge}
                                            >
                                                {taskCount}
                                            </span>
                                        )}
                                </Button>
                            )}
                            <ShowHideToggleButton
                                checked={
                                    !checkedState(
                                        {
                                            label: classicWorkspaceLabel,
                                            stateKey:
                                                'showHideStates.betaWorkspace',
                                        },
                                        currentState,
                                        selectedProjectNode?.isInProject
                                    )
                                }
                                label={classicWorkspaceLabel}
                                isWorkspaceToggle={true}
                                labelPlacement="start"
                                onChange={() =>
                                    handleToggleSwitchValueChange(
                                        'showHideStates.betaWorkspace'
                                    )
                                }
                            />
                        </GridItem>
                    </GridContainer>
                </ContainerMaxWidthXl>
            </AppBar>
        </React.Fragment>
    )
}
export default Workspace2ActionBar
